import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRightOutlined,
  CheckCircleFilled,
  CloudServerOutlined,
  DesktopOutlined,
  GlobalOutlined,
  LaptopOutlined,
  MobileOutlined,
  SafetyCertificateOutlined,
  SoundOutlined,
  TeamOutlined,
  WechatOutlined
} from '@ant-design/icons';
import PublicHeader from '../components/layout/PublicHeader';
import '../assets/css/ProductSuitePage.css';

const products = [
  {
    key: 'web',
    detailKey: 'web-management',
    number: '01',
    name: '网页管理后台',
    role: '学校与教师网页端',
    icon: <GlobalOutlined />,
    summary: '通过浏览器统一管理学校、班级、教师、学生、设备和业务数据，无需安装即可进入。',
    features: ['学校与班级管理', '教师、学生和家长数据', '广播任务与发送记录', '设备状态与系统配置'],
    scene: '适合学校管理员和教师在浏览器中完成配置、查询和集中管理。',
    tone: 'purple'
  },
  {
    key: 'workbench',
    detailKey: 'teacher-workbench',
    number: '02',
    name: '教师工作台',
    role: '教师主工作端',
    icon: <LaptopOutlined />,
    summary: '面向教师完整工作流程的桌面工作空间，把学校任务、教学、班级、学生与家校协同集中在一个界面。',
    features: ['学校通知与任务', '广播与远程看班', '教学、班级和学生管理', '家校协同与数据收集'],
    scene: '适合教师在电脑前进行日常集中处理和持续管理。',
    tone: 'blue'
  },
  {
    key: 'classroom',
    detailKey: 'classroom-desktop',
    number: '03',
    name: '教室桌面客户端',
    role: '教室现场执行端',
    icon: <SoundOutlined />,
    summary: '常驻教室电脑，接收教师和学校发送的信息，让通知、提醒和课堂动作真正到达教室现场。',
    features: ['实时广播与定时提醒', '横幅、弹窗和全屏展示', '文字、语音、图片与视频', '座位表、智能考勤与设备状态'],
    scene: '适合班级大屏、讲台电脑和教室固定设备。',
    tone: 'green'
  },
  {
    key: 'wechat',
    detailKey: 'wechat-mini-program',
    number: '04',
    name: '微信小程序',
    role: '微信便捷端',
    icon: <WechatOutlined />,
    summary: '无需安装，教师和家长可直接从微信进入，在移动场景中完成高频、即时的班级操作。',
    features: ['班级、学生与广播', '教师工具和扫码场景', '家长绑定与作业反馈', '同步便签与个人设置'],
    scene: '适合随时查看、快速处理、扫码和家校互动。',
    tone: 'wechat'
  },
  {
    key: 'mobile',
    detailKey: 'mobile-app',
    number: '05',
    name: '手机 APP',
    role: 'iOS 与 Android 移动端',
    icon: <MobileOutlined />,
    summary: '一套移动主工程覆盖 iPhone、iPad、安卓手机和平板，让教师和家长在移动场景中持续处理业务。',
    features: ['教师与家长角色空间', '班级、学生与广播', '扫码、拍照、录音和文件', '学校通知、任务与反馈'],
    scene: 'iOS 与 Android 共用业务代码，当前处于签名、证书和真机发布准备阶段。',
    status: '发布准备中',
    tone: 'orange'
  }
];

const scenarios = [
  {
    icon: <TeamOutlined />,
    title: '教师统一管理',
    text: '学校和教师在网页后台或工作台集中管理，在小程序和 APP 处理移动任务。'
  },
  {
    icon: <SoundOutlined />,
    title: '课堂即时触达',
    text: '广播和提醒由教师发出，通过服务器实时到达教室桌面客户端。'
  },
  {
    icon: <MobileOutlined />,
    title: '家校移动协同',
    text: '家长通过小程序或手机 APP 绑定学生，接收信息并完成作业、评价和资料反馈。'
  },
  {
    icon: <SafetyCertificateOutlined />,
    title: '统一账号与权限',
    text: '五端连接同一学校、班级和教师身份，数据访问继续遵循服务器权限规则。'
  }
];

const ProductSuitePage = () => {
  const navigate = useNavigate();

  return (
    <div className="suite-page">
      <PublicHeader />

      <main>
        <section className="suite-hero">
          <div className="suite-hero-copy">
            <div className="suite-eyebrow"><span>五端协同产品体系</span><em>ONE ACCOUNT · FIVE EXPERIENCES</em></div>
            <h1>一个账号，连接教师、移动端与每一间教室</h1>
            <p>教室小喇叭不是五个相互独立的软件，而是一套围绕学校、教师、班级、学生和家长持续协同的产品体系。</p>
            <div className="suite-hero-actions">
              <button className="suite-primary-button" type="button" onClick={() => document.getElementById('suite-products')?.scrollIntoView({ behavior: 'smooth' })}>
                查看五端产品 <ArrowRightOutlined />
              </button>
              <button className="suite-secondary-button" type="button" onClick={() => navigate('/product')}>了解教室小喇叭</button>
            </div>
            <div className="suite-trust-row">
              <span><CheckCircleFilled /> 同一账号体系</span>
              <span><CheckCircleFilled /> 同一学校与班级数据</span>
              <span><CheckCircleFilled /> 服务器统一权限</span>
            </div>
          </div>

          <div className="suite-hero-visual" aria-label="五端协同关系">
            <div className="suite-core">
              <img src="/xlb-logo.png" alt="" />
              <strong>教室小喇叭</strong>
              <span>统一服务与数据</span>
            </div>
            <div className="suite-orbit suite-orbit-one"><GlobalOutlined /><span>网页后台</span></div>
            <div className="suite-orbit suite-orbit-two"><LaptopOutlined /><span>工作台</span></div>
            <div className="suite-orbit suite-orbit-three"><DesktopOutlined /><span>教室端</span></div>
            <div className="suite-orbit suite-orbit-four"><WechatOutlined /><span>小程序</span></div>
            <div className="suite-orbit suite-orbit-five"><MobileOutlined /><span>手机 APP</span></div>
          </div>
        </section>

        <section className="suite-product-section" id="suite-products">
          <div className="suite-section-heading">
            <span>产品矩阵</span>
            <h2>五个端，各自解决一个关键场景</h2>
            <p>桌面端负责深度工作，移动端负责随时处理，教室端负责现场执行，所有数据回到同一服务体系。</p>
          </div>
          <div className="suite-product-grid">
            {products.map((product) => (
              <article
                className={`suite-product-card ${product.tone}`}
                key={product.key}
                tabIndex={0}
                role="link"
                onClick={() => navigate(`/products/${product.detailKey}`)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    navigate(`/products/${product.detailKey}`);
                  }
                }}
              >
                <div className="suite-card-top">
                  <span className="suite-product-number">{product.number}</span>
                  <span className="suite-product-icon">{product.icon}</span>
                </div>
                <div className="suite-product-title">
                  <div>
                    <h3>{product.name}</h3>
                    <span>{product.role}</span>
                  </div>
                  {product.status && <em>{product.status}</em>}
                </div>
                <p className="suite-product-summary">{product.summary}</p>
                <ul>
                  {product.features.map((feature) => <li key={feature}><CheckCircleFilled /> {feature}</li>)}
                </ul>
                <p className="suite-product-scene">{product.scene}</p>
                <button
                  className="suite-product-link"
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    navigate(`/products/${product.detailKey}`);
                  }}
                >
                  查看独立产品介绍 <ArrowRightOutlined />
                </button>
              </article>
            ))}
          </div>
        </section>

        <section className="suite-flow-section">
          <div className="suite-section-heading light">
            <span>协同方式</span>
            <h2>五端共享一套业务，而不是重复建设五套系统</h2>
          </div>
          <div className="suite-flow">
            <div className="suite-flow-node"><LaptopOutlined /><strong>集中管理与深度工作</strong><span>网页管理后台 · 教师工作台</span></div>
            <ArrowRightOutlined className="suite-flow-arrow" />
            <div className="suite-flow-node"><MobileOutlined /><strong>移动即时处理</strong><span>微信小程序 · 手机 APP</span></div>
            <ArrowRightOutlined className="suite-flow-arrow" />
            <div className="suite-flow-node"><CloudServerOutlined /><strong>统一服务与权限</strong><span>学校 · 班级 · 教师 · 学生</span></div>
            <ArrowRightOutlined className="suite-flow-arrow" />
            <div className="suite-flow-node"><DesktopOutlined /><strong>教室现场执行</strong><span>教室桌面客户端</span></div>
          </div>
        </section>

        <section className="suite-scenario-section">
          <div className="suite-section-heading">
            <span>核心价值</span>
            <h2>让信息到达、工作衔接、数据沉淀</h2>
          </div>
          <div className="suite-scenario-grid">
            {scenarios.map((scenario) => (
              <article key={scenario.title}>
                <div>{scenario.icon}</div>
                <h3>{scenario.title}</h3>
                <p>{scenario.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="suite-cta">
          <div>
            <span>教室小喇叭 · www.810086.com</span>
            <h2>从教师的一次操作，到教室和家长的真实响应</h2>
            <p>五端围绕同一套学校与班级数据协同，让产品随教师工作场景自然流转。</p>
          </div>
          <div className="suite-cta-actions">
            <button type="button" onClick={() => navigate('/product')}>进入产品首页</button>
            <button className="solid" type="button" onClick={() => navigate('/register')}>注册账号</button>
          </div>
        </section>
      </main>

      <footer className="suite-footer">
        <span>教室小喇叭 © 2026</span>
        <span>网页管理后台 · 教师工作台 · 教室桌面客户端 · 微信小程序 · 手机 APP</span>
      </footer>
    </div>
  );
};

export default ProductSuitePage;
